import React from 'react';
import {
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import {CLASSIFICATION_LEVELS} from '../prosemirror-editor/helpers/classificationLevels';
import {getCompartmentOptions, getDistributionOptions} from '../prosemirror-editor/helpers/classificationOptions';

export const DISTRIBUTION_OPTIONS = getDistributionOptions();
export const COMPARTMENT_OPTIONS = getCompartmentOptions();

/**
 * Generic multi-select dropdown for classification distribution/compartment markings.
 * Shared by the six File/URL/Raw-text import dialogs via `ClassificationFieldsGroup`.
 *
 * @param {boolean} [hideLabel=false] - When true, renders without an InputLabel / notch so the
 *   control is more compact (used in table rows where column headers already provide context).
 */
export function ClassificationMultiSelect({label, value, options, onChange, idPrefix = label, hideLabel = false}) {
    return (
        <FormControl size="small" fullWidth>
            {!hideLabel && <InputLabel id={`${idPrefix}-label`}>{label}</InputLabel>}
            <Select
                labelId={hideLabel ? undefined : `${idPrefix}-label`}
                multiple
                value={value}
                onChange={(event) => {
                    const next = event.target.value;
                    onChange(typeof next === 'string' ? next.split(',') : next);
                }}
                input={hideLabel ? <OutlinedInput notched={false}/> : <OutlinedInput label={label}/>}
                MenuProps={{sx: {zIndex: 1500}}}
                renderValue={(selected) => (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {selected.map((v) => {
                            const opt = options.find((o) => o.value === v);
                            return <Chip key={v} size="small" label={opt ? opt.value : v}/>;
                        })}
                    </Stack>
                )}
                displayEmpty
            >
                {!hideLabel ? null : (
                    <MenuItem disabled value="" sx={{fontStyle: 'italic', color: 'text.disabled'}}>
                        {label}
                    </MenuItem>
                )}
                {options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox checked={value.includes(opt.value)} size="small"/>
                        <ListItemText primary={opt.label}/>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

/**
 * Shared Classification Level + Compartment + Distribution field group.
 *
 * Used everywhere a "source" (file / URL / pasted content) is added — the Sources panel's
 * `SourcesFileImportDialog` / `SourcesUrlImportDialog` / `SourcesPasteImportDialog` (analyst)
 * and the Collection Material dialogs' `CollectionMaterialFileImportDialog` /
 * `CollectionMaterialUrlImportDialog` / `CollectionMaterialPasteImportDialog` (data collector)
 * — so the same markings catalog and UI show up consistently across both surfaces. All three
 * fields are optional: Classification Level defaults to 'U' when unset; Distribution/Compartment
 * default to an empty ("None") selection. Pair with `useClassificationFields` for the
 * accompanying state/reset/payload boilerplate.
 */
export default function ClassificationFieldsGroup({
    idPrefix = 'classification',
    level,
    onLevelChange,
    distribution,
    onDistributionChange,
    compartment,
    onCompartmentChange,
}) {
    return (
        <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
                Classification
            </Typography>
            <Stack direction="row" spacing={2}>
                <FormControl size="small" fullWidth>
                    <InputLabel id={`${idPrefix}-level-label`}>Classification Level</InputLabel>
                    <Select
                        labelId={`${idPrefix}-level-label`}
                        value={level}
                        onChange={(event) => onLevelChange(event.target.value)}
                        input={<OutlinedInput label="Classification Level"/>}
                        MenuProps={{sx: {zIndex: 1500}}}
                        renderValue={(value) => {
                            const found = CLASSIFICATION_LEVELS.find((l) => l.value === value);
                            return found ? found.label : value;
                        }}
                    >
                        {CLASSIFICATION_LEVELS.map((lvl) => (
                            <MenuItem key={lvl.value} value={lvl.value} sx={{color: lvl.color}}>
                                {lvl.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <ClassificationMultiSelect
                    idPrefix={`${idPrefix}-compartment`}
                    label="Compartment"
                    value={compartment}
                    options={COMPARTMENT_OPTIONS}
                    onChange={onCompartmentChange}
                />
                <ClassificationMultiSelect
                    idPrefix={`${idPrefix}-distribution`}
                    label="Distribution"
                    value={distribution}
                    options={DISTRIBUTION_OPTIONS}
                    onChange={onDistributionChange}
                />
            </Stack>
        </Stack>
    );
}
